import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Youtube, Mail, Phone, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

// Store Accordion Item
const StoreAccordion = ({ store, isOpen, onToggle }) => {
  const { getLocalizedField } = useLanguage();
  
  return (
    <div className="border-b border-vav-border/50 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-2 text-left text-vav-fg-muted hover:text-vav-gold transition-colors text-sm"
      >
        <span>{getLocalizedField(store, 'name') || store.name}</span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-4 space-y-2 text-xs text-vav-fg-dark">
              {store.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3 h-3 text-vav-gold mt-0.5 flex-shrink-0" />
                  <span>{store.address}</span>
                </div>
              )}
              {store.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-vav-gold flex-shrink-0" />
                  <a href={`tel:${store.phone}`} className="hover:text-vav-gold transition-colors">
                    {store.phone}
                  </a>
                </div>
              )}
              {store.mapUrl && (
                <a 
                  href={store.mapUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-vav-gold hover:underline"
                >
                  Haritada Göster →
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Footer = () => {
  const { getSetting } = useSiteSettings();
  const { t, getLocalizedField } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [stores, setStores] = useState([]);
  const [openStoreId, setOpenStoreId] = useState(null);

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`);
        const data = await response.json();
        setStores(data.stores || []);
      } catch (err) {
        console.error('Error fetching stores:', err);
      }
    };
    fetchStores();
  }, []);

  // Get CMS values
  const companyName = getSetting('company_name', 'VAV Wear');
  const address = getSetting('address', 'İstanbul, Türkiye');
  const phone = getSetting('phone', '+90 850 123 45 67');
  const email = getSetting('email', 'info@vavwear.com');
  const whatsapp = getSetting('whatsapp', '');
  const instagram = getSetting('instagram', '');
  const facebook = getSetting('facebook', '');
  const twitter = getSetting('twitter', '');
  const youtube = getSetting('youtube', '');
  const logoLight = getSetting('logo_light', 'https://customer-assets.emergentagent.com/job_cd0460ea-ffc2-439c-bd21-54fcb026442d/artifacts/xso0m373_logo.png');
  const brandSlogan = getSetting('brand_slogan', 'Premium taktik ve outdoor giyim. Zorlu koşullar için tasarlandı.');
  const brandSloganEn = getSetting('brand_slogan_en', 'Premium tactical and outdoor wear. Designed for tough conditions.');

  return (
    <footer className="bg-vav-bg-paper border-t border-vav-border">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img 
              src={logoLight} 
              alt={companyName} 
              className="h-12 w-auto mb-6"
            />
            <p className="text-vav-fg-muted text-sm leading-relaxed mb-6">
              {t(brandSlogan, brandSloganEn)}
            </p>
            <div className="flex gap-4">
              {instagram && (
                <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-vav-fg-muted hover:text-vav-gold transition-colors" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {facebook && (
                <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-vav-fg-muted hover:text-vav-gold transition-colors" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {twitter && (
                <a href={twitter} target="_blank" rel="noopener noreferrer" className="text-vav-fg-muted hover:text-vav-gold transition-colors" aria-label="Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {youtube && (
                <a href={youtube} target="_blank" rel="noopener noreferrer" className="text-vav-fg-muted hover:text-vav-gold transition-colors" aria-label="Youtube">
                  <Youtube className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Stores - Accordion */}
          <div>
            <h4 className="font-heading text-lg uppercase tracking-wider text-vav-fg mb-6">
              {t('Mağazalarımız', 'Our Stores')}
            </h4>
            {stores.length > 0 ? (
              <div className="space-y-1">
                {stores.map((store) => (
                  <StoreAccordion
                    key={store.id}
                    store={store}
                    isOpen={openStoreId === store.id}
                    onToggle={() => setOpenStoreId(openStoreId === store.id ? null : store.id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-vav-fg-muted text-sm">
                {t('Mağaza bilgisi yükleniyor...', 'Loading store info...')}
              </p>
            )}
            <Link 
              to="/contact" 
              className="inline-block mt-4 text-sm text-vav-gold hover:underline"
            >
              {t('Tüm mağazaları gör', 'View all stores')} →
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg uppercase tracking-wider text-vav-fg mb-6">
              {t('Hızlı Linkler', 'Quick Links')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="https://www.taktikstore.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-vav-fg-muted hover:text-vav-gold transition-colors text-sm"
                >
                  {t('Online Mağaza', 'Online Store')}
                </a>
              </li>
              <li>
                <Link to="/about" className="text-vav-fg-muted hover:text-vav-gold transition-colors text-sm">
                  {t('Hakkımızda', 'About Us')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-vav-fg-muted hover:text-vav-gold transition-colors text-sm">
                  {t('İletişim', 'Contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg uppercase tracking-wider text-vav-fg mb-6">
              {t('İletişim', 'Contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-vav-gold flex-shrink-0 mt-0.5" />
                <span className="text-vav-fg-muted text-sm whitespace-pre-line">
                  {address}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-vav-gold flex-shrink-0" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-vav-fg-muted hover:text-vav-gold transition-colors text-sm">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-vav-gold flex-shrink-0" />
                <a href={`mailto:${email}`} className="text-vav-fg-muted hover:text-vav-gold transition-colors text-sm">
                  {email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-vav-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-vav-fg-muted text-xs">
              © {currentYear} {companyName}. {t('Tüm hakları saklıdır.', 'All rights reserved.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/privacy" className="text-vav-fg-muted hover:text-vav-gold transition-colors text-xs">
                {t('Gizlilik Politikası', 'Privacy Policy')}
              </Link>
              <Link to="/terms" className="text-vav-fg-muted hover:text-vav-gold transition-colors text-xs">
                {t('Kullanım Koşulları', 'Terms of Use')}
              </Link>
              <Link to="/kvkk" className="text-vav-fg-muted hover:text-vav-gold transition-colors text-xs">
                KVKK
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
